import React, { useEffect, useMemo, useRef, useState } from "react";
import { GetFeaturedOrTopItemsParams } from "@/api/interfaces/items.interface";
import { GetFeaturedOrTopItems } from "@/api/items";
import { useFetcher } from "@/api/utils";
import { StoreInterface, WABotSession } from "@/assets/interfaces";
import { computeItemAvailability, getStoreFrontSEOTags } from "@/assets/js/utils/utils";
import Portal from "@/components/portal";
import SEOTags from "@/components/seo-tags";
import StoreFrontErrors from "@/components/storefronts-v2/ui/store-errors";
import StoreMaintenanceMode from "@/components/storefronts-v1/ui/store-maintenance-mode";
import SlugDisabledMode from "@/components/storefronts-v1/ui/slug-disabled-mode";
import Head from "next/head";
import { useRouter } from "next/router";
import classNames from "classnames";
import {
  Header,
  Footer,
  Reviews,
  Categories,
  Cart,
  ItemsGridList,
  HeaderCategories,
} from "@/components/storefronts-v2/sections";
import Highlights from "@/components/storefronts-v2/highlights";
import FloatingCartAction from "./ui/floating-cart-action";
import { ProductDetailsModal } from "./modals";
import StorefrontContext, { VisitRoute } from "@/contexts/storefront";
import useStorefrontsInitializer from "../hooks/useStorefrontsInitializer";
import CartContextV2 from "@/contexts/cart-context-v2";
import SwitchCategories from "./ui/switch-categories";

type ModalKeys =
  | "explainer"
  | "product_details"
  | "search_modal"
  | "about_store"
  | "refund_policy"
  | "delivery_fees"
  | "more_info"
  | "image_details"
  | "about_images";

export interface StoreFrontProps {
  error: boolean;
  data?: {
    store?: StoreInterface;
    category?: string;
    search?: string;
  };
  notFound: boolean;
  disabled_slug?: boolean;
  slug?: string;
  query?: { [key: string]: string };
  isMini?: boolean;
  botSession?: WABotSession;
  visitRoute?: VisitRoute;
}

const StoreFrontV2: React.FC<StoreFrontProps> = (props) => {
  const router = useRouter();
  const { error, data, notFound, disabled_slug, slug, query, botSession } = props;
  const store = data?.store;

  const { paths } = StorefrontContext.useContainer();

  const {
    fullySetup,
    modals,
    toggleModal,
    fetchStoreBankAccountReq,
    openItem,
    selectedItem,
    maintenanceMode,
    storeIsOpen,
    openItemFromModal,
  } = useStorefrontsInitializer(store, query);

  const featuredOrTopItemsReq = useFetcher<GetFeaturedOrTopItemsParams>(
    GetFeaturedOrTopItems,
    {
      store: store?.id,
    },
    [store?.id]
  );
  const featuredItems = featuredOrTopItemsReq?.response?.data?.items ?? [];

  const formattedFeaturedItems = useMemo(() => {
    return featuredItems.map((i) => computeItemAvailability(i));
  }, [featuredItems]);

  if (notFound || error || !store) {
    return <StoreFrontErrors {...{ notFound, slug, error, store: store }} />;
  }

  if (disabled_slug) {
    return (
      <>
        <SEOTags tags={getStoreFrontSEOTags(slug, store)} storeLogo={store?.logo} />
        <SlugDisabledMode store={store} />
      </>
    );
  }

  if (maintenanceMode) {
    return (
      <>
        <SEOTags tags={getStoreFrontSEOTags(slug, store)} storeLogo={store?.logo} />
        <StoreMaintenanceMode store={store} />
      </>
    );
  }

  return (
    <>
      <SEOTags tags={getStoreFrontSEOTags(slug, store)} storeLogo={store?.logo} />
      <Head>
        <link href="https://fonts.googleapis.com/css2?family=Koulen&display=swap" rel="stylesheet" />
      </Head>
      <CartContextV2.Provider
        initialState={{
          storeId: store.id,
          cartRef: store.id,
          account: fetchStoreBankAccountReq?.response?.data,
          currencySettings: store?.currencies
            ? {
                active: store?.currencies?.storefront_default,
                rates: store?.currencies?.rates,
                options: store?.currencies?.storefront,
                defaultCurrency: store?.currencies?.products,
                allRates: store["meta"]?.["rates"]?.rates ?? {},
                rateId: store["meta"]?.["rates"]?.id ?? "",
                markups: store?.currencies?.rates,
              }
            : undefined,
        }}
      >
        {fullySetup && (
          <div>
            <Header
              resetPage={() => {}}
              store={store}
              mode="light"
              toggleSearch={() => toggleModal("search_modal")}
              modals={modals}
              toggle={(key: ModalKeys) => toggleModal(key)}
              openItem={openItem}
            />

            <main className="">
              {store?.highlights_config?.should_show_highlights && store?.highlights_config?.count > 0 && (
                <div className="py-3.75">
                  <Highlights expectedHighlights={store?.highlights_config?.count} store={store} openItem={openItem} />
                </div>
              )}
              <div className={classNames("w-full px-5 sm:px-9 md:px-12.5 mx-auto")}>
                {store?.configuration?.top_category_cards !== false && store?.categories?.length >= 2 && (
                  <HeaderCategories storeId={store?.id} store={store?.slug} />
                )}
                <div
                  className={classNames(
                    "store-container py-6.25 sm:py-10",
                    store?.highlights_config?.should_show_highlights && store?.highlights_config?.count > 0
                      ? "border-t border-grey-divider"
                      : ""
                  )}
                >
                  <div>
                    <div className="flex justify-between items-center mb-5 sm:mb-4.5 lg:mb-5">
                      <h3 className="text-black text-lg sm:text-xl md:text-xl font-bold flex items-center">
                        {featuredOrTopItemsReq?.response?.data?.type === "top" ? "Hottest" : "Featured"} Items
                      </h3>
                      {/* <button
                        onClick={() => router.push(paths.products)}
                        className="ap-1.5 items-center text-sm sm:text-1sm font-display font-semibold md:text-base flex text-primary-500"
                      >
                        All Products
                        prettier-ignore
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" >
                          <path d="M3.75 9H14.25M14.25 9L9 3.75M14.25 9L9 14.25" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                      </button> */}
                      <SwitchCategories categories={data.store.categories} itemsLoading={false} resetPage={() => {}} />
                    </div>
                    <ItemsGridList
                      items={formattedFeaturedItems}
                      productsLoading={featuredOrTopItemsReq.isLoading}
                      setSelectedItem={openItem}
                      emptyItemsCount={10}
                    />
                  </div>
                </div>
              </div>
            </main>

            <Categories store={store} />
            <Reviews reviews={store.testimonials} />

            <Footer store={store} toggle={(key: ModalKeys) => toggleModal(key)} />

            <Portal>
              {selectedItem && (
                <ProductDetailsModal
                  show={modals.product_details}
                  toggle={() => toggleModal("product_details")}
                  item={selectedItem}
                  setSelectedItem={openItemFromModal}
                />
              )}
              <Cart storeIsOpen={storeIsOpen.open} phoneNumber={data.store.phone} store={store} />
              <FloatingCartAction productShown={modals.product_details} />
            </Portal>
          </div>
        )}

        {!fullySetup && (
          <div className="relative h-screen overflow-hidden flex flex-col items-center justify-center">
            <div className="spinner spinner--md text-black-secondary"></div>
          </div>
        )}
      </CartContextV2.Provider>
    </>
  );
};

export default StoreFrontV2;
